import React, { useEffect, useMemo, useState } from 'react';
import Navbar from '../../components/layout/Navbar';
import Footer from '../../components/layout/Footer';
import {
  ACCOUNT_SEARCH_ENDPOINT,
  USERS_MARKETING_LIST_ENDPOINT,
  WABA_ACCESS_LIST_ENDPOINT,
  WABA_ACCESS_SAVE_ENDPOINT,
  WABA_ACCOUNT_ASSIGNEES_MAP_ENDPOINT, // ✅ new
} from '../../config/api';
import { decryptData } from '../../utils/encrypt';

function WabaAssignment() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [users, setUsers] = useState([]);            // Marketing users [{id, name}]
  const [selectedUserId, setSelectedUserId] = useState(0);

  const [search, setSearch] = useState('');
  const [accounts, setAccounts] = useState([]);      // [{id, account_name, city, created_by_name, updated_by_name, assignees, assignee_ids}]
  const [selectedAccountIds, setSelectedAccountIds] = useState(new Set()); // assigned to selected user

  const [onlyAccessible, setOnlyAccessible] = useState(false);

  // Pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // --- Helpers ---
  const getAuth = () => {
    const enc = localStorage.getItem('suyog_user');
    if (!enc) return null;
    try { return decryptData(enc); } catch { return null; }
  };

  // Filtered (by search & toggle)
 const filteredAccounts = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = accounts;

    // text filter
    if (q) {
      list = list.filter(a =>
        (a.account_name || '').toLowerCase().includes(q) ||
        (a.city || '').toLowerCase().includes(q)
      );
    }

    // "Show only" filter
    if (onlyAccessible) {
      list = list.filter(a => {
        const idNum = Number(a.id);
        // 1) use the live per-user selection set
        if (selectedAccountIds.has(idNum)) return true;
        // 2) fallback to the assignee map on the row (numbers)
        if (Array.isArray(a.assignee_ids) && selectedUserId) {
          return a.assignee_ids.includes(Number(selectedUserId));
        }
        return false;
      });
    }

    return list;
  }, [accounts, search, onlyAccessible, selectedAccountIds, selectedUserId]);

  // Pagination derived
  const total = filteredAccounts.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(page, totalPages);
  const start = (safePage - 1) * pageSize;
  const end = start + pageSize;
  const pageRows = filteredAccounts.slice(start, end);

  // --- Load Marketing users ---
  useEffect(() => {
    const auth = getAuth();
    if (!auth) {
      alert('User not authenticated');
      setLoading(false);
      return;
    }
    const org_id = auth?.selected_org?.value || auth?.org_id || 0;
    const meId = Number(auth?.user_id || 0);

    fetch(USERS_MARKETING_LIST_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ org_id })
    })
      .then(r => r.json())
      .then(data => {
        if (!Array.isArray(data)) {
          console.error('Unexpected marketing list response', data);
          setUsers([]);
          setSelectedUserId(0);
          return;
        }
        const arr = data.map(u => ({
          id: Number(u.id),
          name: u.name || u.full_name || u.username || `User #${u.id}`
        }));
        setUsers(arr);
        const meInList = arr.find(u => u.id === meId);
        setSelectedUserId(meInList ? meId : (arr[0]?.id || 0));
      })
      .catch(err => {
        console.error('Load marketing users error:', err);
        setUsers([]);
        setSelectedUserId(0);
      })
      .finally(() => setLoading(false));
  }, []);

  // --- Load accounts (all) once + merge assignees map ---
  useEffect(() => {
    const loadAccountsAndMap = async () => {
      const auth = getAuth();
      if (!auth) return;
      const user_id = auth?.user_id || 0;
      const org_id = auth?.selected_org?.value || auth?.org_id || 0;

      try {
        // 1) Accounts baseline
        const res = await fetch(ACCOUNT_SEARCH_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ query: '', user_id })
        });
        const data = await res.json();
        let accs = Array.isArray(data) ? data.map(a => ({ ...a, id: Number(a.id) })) : [];

        // 2) Assignees map
        const mapRes = await fetch(WABA_ACCOUNT_ASSIGNEES_MAP_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ org_id }) // optional filter by org if your backend supports it
        });
        const mapData = await mapRes.json();
        const m = {};
        if (mapData && Array.isArray(mapData.rows)) {
          for (const row of mapData.rows) {
            m[Number(row.account_id)] = {
              assignees: row.assignees || '',
              assignee_ids: Array.isArray(row.assignee_ids) ? row.assignee_ids.map(Number) : []
            };
          }
        }

        // 3) Merge
        accs = accs.map(a => ({
          ...a,
          assignees: m[a.id]?.assignees || '',
          assignee_ids: m[a.id]?.assignee_ids || []
        }));

        setAccounts(accs);
      } catch (e) {
        console.error('Load accounts/map error:', e);
      }
    };
    loadAccountsAndMap();
  }, []);

  // --- Load current WABA access for selected user (to toggle per-user assignment) ---
  useEffect(() => {
    const auth = getAuth();
    if (!auth || !selectedUserId) {
      setSelectedAccountIds(new Set());
      return;
    }
    const user_id = auth?.user_id || 0;

    fetch(WABA_ACCESS_LIST_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ selected_user_id: selectedUserId, requested_by: user_id })
    })
      .then(r => r.json())
      .then(data => {
        const ids = Array.isArray(data?.account_ids) ? data.account_ids.map(Number) : [];
        setSelectedAccountIds(new Set(ids));
        setPage(1);
      })
      .catch(err => {
        console.error('Load WABA access error:', err);
        setSelectedAccountIds(new Set());
      });
  }, [selectedUserId]);

  // --- Toggle one account id for the currently selected user ---
  const toggleAccount = (aid) => {
    setSelectedAccountIds(prev => {
      const s = new Set(prev);
      if (s.has(aid)) s.delete(aid); else s.add(aid);
      return s;
    });
  };

  // --- Bulk actions in filtered view ---
  const selectAllFiltered = () => {
    setSelectedAccountIds(prev => {
      const s = new Set(prev);
      filteredAccounts.forEach(a => s.add(a.id));
      return s;
    });
  };
  const clearAllFiltered = () => {
    setSelectedAccountIds(prev => {
      const s = new Set(prev);
      filteredAccounts.forEach(a => s.delete(a.id));
      return s;
    });
  };

  // --- Header checkbox: select/clear all on current page ---
  const pageAllChecked = pageRows.length > 0 && pageRows.every(a => selectedAccountIds.has(a.id));
  const pageIndeterminate = pageRows.some(a => selectedAccountIds.has(a.id)) && !pageAllChecked;

  const togglePageAll = () => {
    setSelectedAccountIds(prev => {
      const s = new Set(prev);
      if (pageAllChecked) {
        pageRows.forEach(a => s.delete(a.id));
      } else {
        pageRows.forEach(a => s.add(a.id));
      }
      return s;
    });
  };

  // --- Save mapping (for the selected user) + refresh the assignees column ---
  const handleSave = async () => {
    const auth = getAuth();
    if (!auth) {
      alert('User not authenticated');
      return;
    }
    if (!selectedUserId) {
      alert('Please select a Marketing user first.');
      return;
    }
    const requested_by = auth?.user_id || 0;
    const account_ids = Array.from(selectedAccountIds);

    try {
      setSaving(true);
      const res = await fetch(WABA_ACCESS_SAVE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ selected_user_id: selectedUserId, account_ids, requested_by })
      });
      const data = await res.json();
      if (res.ok && data?.success) {
        alert('WABA access saved successfully.');
        // Refresh the assignees map so the “Assigned To” column reflects changes
        const org_id = auth?.selected_org?.value || auth?.org_id || 0;
        const mapRes = await fetch(WABA_ACCOUNT_ASSIGNEES_MAP_ENDPOINT, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ org_id })
        });
        const mapData = await mapRes.json();
        const m = {};
        if (mapData && Array.isArray(mapData.rows)) {
          for (const row of mapData.rows) {
            m[Number(row.account_id)] = {
              assignees: row.assignees || '',
              assignee_ids: Array.isArray(row.assignee_ids) ? row.assignee_ids.map(Number) : []
            };
          }
        }
        setAccounts(prev => prev.map(a => ({
          ...a,
          assignees: m[a.id]?.assignees ?? a.assignees ?? '',
          assignee_ids: m[a.id]?.assignee_ids ?? a.assignee_ids ?? []
        })));
      } else {
        alert(data?.error || 'Failed to save WABA access');
      }
    } catch (e) {
      console.error('Save error:', e);
      alert('Server error while saving');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="container mt-4">Loading…</div>;

  return (
    <>
      <Navbar />
      <div className="container mt-5">
        <div className="card shadow-lg border-0 rounded-4">
          <div
            className="card-header text-white rounded-top-4 d-flex justify-content-between align-items-center"
            style={{ background: 'linear-gradient(90deg, #007bff, #0056b3)', padding: '1rem 1.5rem' }}
          >
            <h4 className="mb-0">Marketing Manager Management</h4>
            <div className="d-flex gap-2">
              <button className="btn btn-success" onClick={handleSave} disabled={saving || !selectedUserId}>
                {saving ? 'Saving…' : '💾 Save'}
              </button>
            </div>
          </div>

          <div className="card-body p-4">
            {/* Controls */}
            <div className="row g-3 align-items-end">
              <div className="col-md-4">
                <label className="form-label">Marketing User</label>
                <select
                  className="form-select"
                  value={selectedUserId || 0}
                  onChange={e => setSelectedUserId(Number(e.target.value))}
                  disabled={users.length === 0}
                >
                  {users.length === 0 && <option value={0}>— No marketing users found —</option>}
                  {users.map(u => (
                    <option key={u.id} value={u.id}>{u.name}</option>
                  ))}
                </select>
              </div>

              <div className="col-md-4">
                <label className="form-label">Search Accounts</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Filter by account or city"
                  value={search}
                  onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                />
              </div>

              <div className="col-md-2">
                <label className="form-label d-block">Page Size</label>
                <select
                  className="form-select"
                  value={pageSize}
                  onChange={(e) => { setPageSize(Number(e.target.value)); setPage(1); }}
                >
                  {[10, 20, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>

              <div hidden className="col-md-2">
                <div className="form-check mt-4">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="onlyAccessible"
                    checked={onlyAccessible}
                    onChange={(e) => { setOnlyAccessible(e.target.checked); setPage(1); }}
                  />
                  <label className="form-check-label" htmlFor="onlyAccessible">
                    Show only assigned
                  </label>
                </div>
              </div>

              <div className="col-12 d-flex align-items-center gap-2">
                <button type="button" className="btn btn-sm btn-outline-primary" onClick={selectAllFiltered}>
                  Select All (filtered)
                </button>
                <button type="button" className="btn btn-sm btn-outline-danger" onClick={clearAllFiltered}>
                  Clear All (filtered)
                </button>
                <div className="ms-auto text-muted small">
                  Showing {total === 0 ? 0 : start + 1}–{Math.min(end, total)} of {total}
                </div>
              </div>
            </div>

            <hr />

            {/* Accounts Table */}
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: 60 }}>
                      {/* Header checkbox: select/clear all on current page */}
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={pageAllChecked}
                        ref={(el) => { if (el) el.indeterminate = pageIndeterminate; }}
                        onChange={togglePageAll}
                        title={pageAllChecked ? 'Unassign all on this page' : 'Assign all on this page'}
                      />
                    </th>
                    <th>Account</th>
                    <th>City</th>
                    <th>Assigned To (Marketing)</th> {/* ✅ new column */}
                    <th>Created By</th>
                    <th>Updated By</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-muted py-4">No accounts match the current filters.</td>
                    </tr>
                  )}

                  {pageRows.map(acc => (
                    <tr key={acc.id} className={selectedAccountIds.has(acc.id) ? 'table-success' : ''}>
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedAccountIds.has(acc.id)}
                          onChange={() => toggleAccount(acc.id)}
                          id={`acc-${acc.id}`}
                        />
                      </td>
                      <td>
                        <label htmlFor={`acc-${acc.id}`} className="mb-0" style={{ cursor: 'pointer' }}>
                          {acc.account_name}
                        </label>
                      </td>
                      <td>{acc.city || '-'}</td>
                      <td className="small">
                        {acc.assignees
                          ? acc.assignees
                              .split(',')
                              .map(s => s.trim())
                              .filter(Boolean)
                              .map((name, i) => (
                                <span key={i} className="badge bg-secondary me-1">{name}</span>
                              ))
                          : <span className="text-muted">—</span>
                        }
                      </td>
                      <td className="text-muted small">{acc.created_by_name || '-'}</td>
                      <td className="text-muted small">{acc.updated_by_name || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="d-flex justify-content-between align-items-center">
              <div className="text-muted small">
                Page {safePage} of {totalPages}
              </div>
              <div className="btn-group">
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage <= 1}
                >
                  ‹ Prev
                </button>
                <button
                  className="btn btn-outline-secondary"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage >= totalPages}
                >
                  Next ›
                </button>
              </div>
            </div>

          </div>
        </div>
      </div>
      <Footer />
    </>
  );
}

export default WabaAssignment;
